import React, { forwardRef, Children } from 'react';
import type { GDSVerticalStackLayoutProps } from '../stack-layout.types';
import { useResponsiveProp } from '../../../hooks/use-responsive-prop';
import { styled } from '../../../theme';

const BaseVerticalStack = styled('div', {
  display: 'flex',
  flex: '1',
  flexDirection: 'column',
  height: '100%',
  variants: {
    horizontalAlignment: {
      start: {
        alignItems: 'flex-start',
      },
      center: {
        alignItems: 'center',
      },
      end: {
        alignItems: 'flex-end',
      },
      stretch: {
        alignItems: 'stretch',
      },
    },
    verticalAlignment: {
      top: {
        justifyContent: 'flex-start',
      },
      center: {
        justifyContent: 'center',
      },
      bottom: {
        justifyContent: 'flex-end',
      },
      spaceAround: {
        justifyContent: 'space-around',
      },
      spaceBetween: {
        justifyContent: 'space-between',
      },
      spaceEvenly: {
        justifyContent: 'space-evenly',
      },
      stretch: {
        justifyContent: 'stretch',
        '& > *': {
          flex: '1 1 0',
        },
      },
    },
  },
});

export const VerticalStackLayout = forwardRef<
  HTMLDivElement,
  GDSVerticalStackLayoutProps
>(
  (
    {
      horizontalAlignment = 'start',
      verticalAlignment = 'top',
      spacing,
      divider,
      children,
      className,
      css,
      ...props
    },
    ref,
  ) => {
    const responsiveSpacing = useResponsiveProp(spacing);
    const responsiveDivider = useResponsiveProp(divider);
    const arrayChildren = Children.toArray(children);

    return (
      <BaseVerticalStack
        horizontalAlignment={horizontalAlignment}
        verticalAlignment={verticalAlignment}
        ref={ref}
        className={['GDS-vertical-stack-layout', className].join(' ')}
        css={{
          ...css,
          ...(spacing && { gap: `$space${responsiveSpacing}` }),
        }}
        {...props}
      >
        {React.Children.map(arrayChildren, (child, index) => {
          const isLast = index === arrayChildren.length - 1;

          return (
            <>
              {child}
              {!isLast && responsiveDivider ? responsiveDivider : null}
            </>
          );
        })}
      </BaseVerticalStack>
    );
  },
);
